import { defineComponent, PropType, reactive, ref } from 'vue';
import { AssociatedPaymentMethodItem } from '@/models/ps-management/multi-methods.model';
import { Delete, Edit } from '@element-plus/icons-vue';
import { useGeoMultiMethodsStore } from '@/store/ps-management/geo-multi-methods.store';
import { ElMessageBox } from 'element-plus';
import { isEqual } from 'lodash';
import FallbackImage from '@/components/fallback-image/fallback-image.vue';

const icons = {
  Delete,
  Edit,
};

export default defineComponent({
  name: 'PaymentMethodItem',
  components: {
    FallbackImage,
  },
  props: {
    modelValue: {
      type: Object as PropType<AssociatedPaymentMethodItem>,
      required: true,
    },
    multiMethodId: {
      type: String,
      required: true,
    },
  },
  emits: ['update:modelValue', 'deleteAssociatedPaymentMethod'],
  setup(props, { emit }) {
    const associatedMethodSettingsFormRules = reactive({
      withdrawalRangeMin: { required: false, message: 'Please enter withdrawal range minimum', trigger: 'blur' },
      withdrawalRangeMax: { required: false, message: 'Please enter withdrawal range maximum', trigger: 'blur' },
    });

    const {
      updateAssociatedBillingPaymentMethod,
      disassociateBillingPaymentMethod,
      setFormChanged,
    } = useGeoMultiMethodsStore();

    const isEditing = ref<boolean>(false);

    const setEditing = (value: boolean) => {
      isEditing.value = value;
    };

    const currentMethodState = ref<AssociatedPaymentMethodItem>({
      ...props.modelValue,
    });

    const deletePaymentMethod = () => {
      if (!props.multiMethodId) {
        return;
      }

      ElMessageBox.confirm(
        'Are you sure you want to delete this method? All data will be permanently lost.',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'error',
          lockScroll: false,
        },
      ).then(() => {
        disassociateBillingPaymentMethod({
          multiMethodId: props.multiMethodId,
          billingPaymentMethodId: props.modelValue.billingPaymentMethodId,
        })
          .then(() => {
            emit('deleteAssociatedPaymentMethod');
          });
      });
    };

    const resetCurrentMethodState = () => {
      if (isEqual(currentMethodState.value, props.modelValue)) {
        setEditing(false);

        return;
      }

      ElMessageBox.confirm(
        'Are you sure you want to discard changes?',
        {
          confirmButtonText: 'Discard',
          cancelButtonText: 'Cancel',
          type: 'warning',
          lockScroll: false,
        },
      ).then(() => {
        setEditing(false);
        currentMethodState.value = {
          ...props.modelValue,
        };
      });
    };

    const updatePaymentMethod = () => {
      updateAssociatedBillingPaymentMethod({
        multiMethodId: props.multiMethodId,
        associatedBillingMethodData: { ...currentMethodState.value },
      })
        .then(() => {
          emit('update:modelValue', currentMethodState.value);
          setEditing(false);
        });
    };

    const setActiveStatus = (isActive: boolean) => {
      const confirmationContent = isActive
        ? {
            text: 'Are you sure you want to activate this payment method?',
            confirmationButtonText: 'Activate',
          }
        : {
            text: 'Are you sure you want to suspend this payment method?',
            confirmationButtonText: 'Suspend',
          };

      ElMessageBox.confirm(
        confirmationContent.text,
        {
          confirmButtonText: confirmationContent.confirmationButtonText,
          cancelButtonText: 'Cancel',
          lockScroll: false,
        },
      ).then(() => {
        updateAssociatedBillingPaymentMethod({
          multiMethodId: props.multiMethodId,
          associatedBillingMethodData: {
            ...currentMethodState.value,
            active: isActive,
          },
        })
          .then(() => {
            currentMethodState.value.active = isActive;
            emit('update:modelValue', currentMethodState.value);
            setEditing(false);
          })
          .catch(() => {
            currentMethodState.value.active = !isActive;
          });
      }).catch(() => {
        currentMethodState.value.active = !isActive;
      });
    };

    return {
      icons,
      isEditing,
      setEditing,
      currentMethodState,
      updatePaymentMethod,
      resetCurrentMethodState,
      deletePaymentMethod,
      setActiveStatus,
      setFormChanged,
      associatedMethodSettingsFormRules,
    };
  },
});
